import { useCallback, useEffect, useRef, useState } from 'react'

// Grace period for crossing the gap between a trigger and the popover before the close starts
export const CLOSE_DELAY_MS = 150
// Must stay >= the `sidebar-popover-out` animation in SidebarFooter.scss, or the popover is
// unmounted before its exit animation finishes
export const EXIT_ANIMATION_MS = 90

export interface HoverPopoverOptions {
  // While true, leaving a trigger does not close the popover — it keeps an in-flight action visible.
  // A close asked for during the hold is remembered and runs as soon as the hold is released.
  holdOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

export interface HoverPopover<T extends string> {
  activeItem: T | null
  // The last item that was opened. Stays put while the popover fades out, so the target and the
  // content do not change underneath the exit animation
  lastItem: T
  exiting: boolean
  // True only when switching between triggers while already open — suppresses the slide on first open
  repositioning: boolean
  // Whether the popover should be mounted, exit animation included
  mounted: boolean
  open: (item: T) => void
  scheduleClose: () => void
  closeNow: () => void
}

export const useHoverPopover = <T extends string>(initialItem: T, { holdOpen = false, onOpenChange }: HoverPopoverOptions = {}): HoverPopover<T> => {
  const [activeItem, setActiveItem] = useState<T | null>(null)
  const [lastItem, setLastItem] = useState<T>(initialItem)
  const [exiting, setExiting] = useState(false)
  const [repositioning, setRepositioning] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const closeRequestedRef = useRef(false)

  useEffect(() => () => clearTimeout(closeTimerRef.current), [])

  useEffect(() => {
    onOpenChange?.(activeItem !== null)
  }, [activeItem, onOpenChange])

  const closeNow = useCallback(() => {
    clearTimeout(closeTimerRef.current)
    closeRequestedRef.current = false
    setActiveItem(null)
    setExiting(false)
    setRepositioning(false)
  }, [])

  const startCloseTimer = useCallback(() => {
    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      // Start the exit animation, then unmount once it has had time to run
      setExiting(true)
      closeTimerRef.current = setTimeout(closeNow, EXIT_ANIMATION_MS)
    }, CLOSE_DELAY_MS)
  }, [closeNow])

  // Flush a close that was asked for while the popover was held open
  useEffect(() => {
    if (holdOpen || !closeRequestedRef.current) {
      return
    }
    closeRequestedRef.current = false
    startCloseTimer()
  }, [holdOpen, startCloseTimer])

  const open = (item: T) => {
    clearTimeout(closeTimerRef.current)
    closeRequestedRef.current = false
    setRepositioning(!exiting && activeItem !== null && lastItem !== item)
    setExiting(false)
    setLastItem(item)
    setActiveItem(item)
  }

  const scheduleClose = () => {
    if (holdOpen) {
      closeRequestedRef.current = true
      return
    }
    startCloseTimer()
  }

  return { activeItem, lastItem, exiting, repositioning, mounted: activeItem !== null || exiting, open, scheduleClose, closeNow }
}
